import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import {
  Users, Package, FileText, BarChart3, Settings, Home, Receipt,
  UserCheck, Menu, X, ChevronLeft, ChevronRight, Archive, LogOut, User, DollarSign, ExternalLink, UserCircle, MessageSquare, CreditCard,
  Image
} from 'lucide-react';

const AdminLayout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  const { user, logout } = useAuth();
  const [newLeadsCount, setNewLeadsCount] = useState(0);

  // Fetch new leads count
  useEffect(() => {
    const fetchNewLeadsCount = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const response = await axios.get('/admin/leads/new-count', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const count = response.data.count || 0;
        setNewLeadsCount(count);
        
        // Update document title
        const baseTitle = 'Admin Portal | IdealTrendz';
        if (count > 0) {
          document.title = `(${count}) New Leads - ${baseTitle}`;
        } else {
          document.title = baseTitle;
        }
      } catch (error) {
        console.error('Failed to fetch new leads count:', error);
      }
    };

    fetchNewLeadsCount();
    const intervalId = setInterval(fetchNewLeadsCount, 30000); // Poll every 30s
    return () => clearInterval(intervalId);
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Admin navigation links with emojis as requested
  const adminNavLinks = [
    { path: '/admin/dashboard', icon: Home, label: 'Dashboard', emoji: '📊' },
    { path: '/admin/employees', icon: UserCheck, label: 'Employees', emoji: '👥' },
    { path: '/admin/products', icon: Package, label: 'Products', emoji: '📦' },
    { path: '/admin/gallery', icon: Image, label: 'Gallery', emoji: '🖼️' },
    { path: '/admin/estimates', icon: FileText, label: 'Estimates', emoji: '📋' },
    { path: '/admin/closed-orders', icon: Archive, label: 'Closed Orders', emoji: '✅' },
    { path: '/admin/invoices', icon: Receipt, label: 'Invoices', emoji: '🧾' },
    { path: '/admin/payments', icon: DollarSign, label: 'Payments', emoji: '💰' },
    { path: '/admin/expenses', icon: CreditCard, label: 'Expenses', emoji: '💸' },
    { path: '/admin/leads', icon: MessageSquare, label: 'Chatbot Leads', emoji: '💬' },
    { path: '/admin/reports', icon: BarChart3, label: 'Reports', emoji: '📈' },
    { path: '/admin/settings', icon: Settings, label: 'Settings', emoji: '⚙️' }
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handleGoToEmployeePortal = () => {
    navigate('/');
  };

  // Helper to get page title based on path
  const getAdminPageTitle = (pathname) => {
    if (pathname.includes('/admin/dashboard')) return { title: 'Admin Dashboard', subtitle: 'Overview and key metrics' };
    if (pathname.includes('/admin/employees')) return { title: 'Employee Management', subtitle: 'Manage staff and permissions' };
    if (pathname.includes('/admin/products')) return { title: 'Product Management', subtitle: 'Manage catalog and inventory' };
    if (pathname.includes('/admin/gallery')) return { title: 'Product Gallery', subtitle: 'Manage product media, brochures and specifications' };
    if (pathname.includes('/admin/estimates')) return { title: 'Estimates Management', subtitle: 'View and manage all estimates' };
    if (pathname.includes('/admin/invoices')) return { title: 'Invoices Management', subtitle: 'Track and manage invoices' };
    if (pathname.includes('/admin/closed-orders')) return { title: 'Closed Orders', subtitle: 'Manage completed orders' };
    if (pathname.includes('/admin/payments')) return { title: 'Payments', subtitle: 'Monitor financial transactions and payment status' };
    if (pathname.includes('/admin/reports')) return { title: 'Reports & Analytics', subtitle: 'View business performance' };
    if (pathname.includes('/admin/settings')) return { title: 'Settings', subtitle: 'System configuration' };
    if (pathname.includes('/admin/profile')) return { title: 'Admin Profile', subtitle: 'Manage your account' };
    if (pathname.includes('/admin/customers')) return { title: 'Customer Management', subtitle: 'View and manage customers' };
    if (pathname.includes('/admin/leads')) return { title: 'Lead Management', subtitle: 'Track and manage potential customers from Chatbot' };
    if (pathname.includes('/admin/expenses')) return { title: 'Expense Management', subtitle: 'Organization-wide project expense tracking' };
    return { title: 'Admin Portal', subtitle: 'Management Console' };
  };

  const pageHeader = getAdminPageTitle(location.pathname);

  return (
    <div className="min-h-screen bg-[#F6F8F5]">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <div
        className="flex h-screen"
        style={{
          paddingTop: user?.isSandbox ? 'var(--sandbox-banner-height)' : '0',
          height: user?.isSandbox ? 'calc(100vh - var(--sandbox-banner-height))' : '100vh'
        }}
      >
        {/* Admin Sidebar - Responsive Design */}
        <div className={`
          fixed lg:static inset-y-0 left-0 z-40
          transform ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 transition-transform duration-300 ease-in-out
          bg-[#0A5F38] border-r border-white/10 shadow-lg flex-shrink-0
          ${sidebarCollapsed ? 'w-[70px]' : 'w-[250px]'
          }
        `}
          style={{
            top: user?.isSandbox ? 'var(--sandbox-banner-height)' : '0',
            height: user?.isSandbox ? 'calc(100vh - var(--sandbox-banner-height))' : '100vh'
          }}
        >
          <div className="flex flex-col h-full">
            {/* Sidebar Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              {!sidebarCollapsed ? (
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden">
                    <img
                      src="https://5.imimg.com/data5/TQ/IX/FZ/SELLER-66617534/logo-90x90.jpg"
                      alt="IDEALTRENDZ Logo"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full bg-[#C5A57D] rounded-lg flex items-center justify-center" style={{ display: 'none' }}>
                      <span className="text-[#0A5F38] font-bold text-xs">IT</span>
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <span className="font-bold text-white text-sm">IDEALTRENDZ</span>
                    <span className="font-semibold text-white/80 text-xs uppercase tracking-wide">ADMIN PANEL</span>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center space-y-2">
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center overflow-hidden">
                    <img
                      src="https://5.imimg.com/data5/TQ/IX/FZ/SELLER-66617534/logo-90x90.jpg"
                      alt="IDEALTRENDZ Logo"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full bg-[#C5A57D] rounded-lg flex items-center justify-center" style={{ display: 'none' }}>
                      <span className="text-white font-bold text-xs">IT</span>
                    </div>
                  </div>
                  <button
                    onClick={toggleSidebar}
                    className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <ChevronRight className="h-4 w-4 text-white/70" />
                  </button>
                </div>
              )}
              {!sidebarCollapsed && (
                <button
                  onClick={toggleSidebar}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <ChevronLeft className="h-5 w-5 text-white/70" />
                </button>
              )}
            </div>

            {/* Navigation Links */}
            <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto no-scrollbar">
              {adminNavLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive(link.path)
                      ? 'bg-[#C5A57D] text-[#0A5F38] font-bold'
                      : 'text-white/90 hover:bg-white/10 hover:text-white'
                      }`}
                    title={sidebarCollapsed ? link.label : ''}
                  >
                    <div className={`relative flex items-center ${sidebarCollapsed ? 'justify-center w-6 h-6' : 'w-6 h-6 mr-3'} flex-shrink-0`}>
                      <Icon className={`h-5 w-5 ${isActive(link.path) ? 'text-[#0A5F38]' : 'text-white/80 group-hover:text-white'
                        }`} />
                      {link.label === 'Chatbot Leads' && newLeadsCount > 0 && sidebarCollapsed && (
                        <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 border border-white"></span>
                        </span>
                      )}
                    </div>
                    {!sidebarCollapsed && (
                      <span className="truncate flex-1">
                        {link.label}
                      </span>
                    )}
                    {!sidebarCollapsed && link.label === 'Chatbot Leads' && newLeadsCount > 0 && (
                      <span className="ml-2 relative flex h-2.5 w-2.5 mt-0.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 shadow-sm"></span>
                      </span>
                    )}
                    {sidebarCollapsed && (
                      <div className="absolute left-full ml-2 bg-gray-900 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                        {link.label}
                      </div>
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Top Navbar */}
          <div className="sticky top-0 z-20 bg-white border-b border-slate-200">
            <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4">
              {/* Left side - Hamburger + Logo/Brand */}
              <div className="flex items-center gap-3">
                {/* Hamburger Menu - Mobile Only */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="Toggle menu"
                >
                  {mobileMenuOpen ? (
                    <X className="h-6 w-6 text-gray-700" />
                  ) : (
                    <Menu className="h-6 w-6 text-gray-700" />
                  )}
                </button>

                {/* Page Title & Subtitle */}
                <div className="hidden md:block">
                  <h1 className="text-lg md:text-xl font-semibold text-slate-900">{pageHeader.title}</h1>
                  <p className="text-xs md:text-sm text-slate-500">{pageHeader.subtitle}</p>
                </div>
                <div className="md:hidden">
                  <h1 className="text-base font-semibold text-slate-900">ADMIN PANEL</h1>
                </div>
              </div>

              {/* Right side - Admin User Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <User className="h-5 w-5 text-gray-600" />
                  <span className="text-sm font-medium text-slate-700">{user?.name || 'Admin'}</span>
                  <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform ${showUserMenu ? 'rotate-90' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-100 py-1 z-50">
                    <button
                      onClick={() => {
                        navigate('/admin/profile');
                        setShowUserMenu(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-slate-900 transition-colors border-b border-gray-100"
                    >
                      <UserCircle className="h-4 w-4 mr-3 text-gray-500" />
                      My Profile
                    </button>
                    <button
                      onClick={handleGoToEmployeePortal}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0A5F38] transition-colors border-b border-gray-100"
                    >
                      <ExternalLink className="h-4 w-4 mr-3 text-gray-500" />
                      Go to Employee Portal
                    </button>
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="h-4 w-4 mr-3 text-red-500" />
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Page Content - Responsive */}
          <div className="flex-1 overflow-auto bg-[#F6F8F5]">
            <div className="px-4 md:px-6 lg:px-8 pb-8 pt-2">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
